import { Group } from '../group/index';
import { InteractionType, ProgressItem, ScoreType } from '../interactions';

export type ArrangeLevel = 'module' | 'chapter' | 'paragraph';
export interface Method {
  code: string;
  name: string;
  slug: string;
  logo?: string;
  language?: string;
  rootLearningLevel?: string;
  arrangeLevel?: ArrangeLevel;
  filterMetadataFields?: Array<string>;
  createdAt: string;
  updatedAt?: string;
  subject?: string;
  subjects?: Array<string>;
  isArrangeable?: boolean;
  nonArrangeableArrangements?: Array<string>;
  hasMathKeyboard?: boolean;
  cardMetadataFields?: Array<string>;
  canArrangeChildren?: boolean;
}

export interface ContentLicense {
  userType: 'USERTYPE_TEACHER' | 'USERTYPE_STUDENT';
  contentRole: 'CONTENT_TOTAL' | 'CONTENT_PROCESSING' | 'CONTENT_START';
  arrangementCode: string;
  cpis: Array<string>;
}

export interface ReferenceMaterialObject {
  name: string;
  code: string;
}

export type ArrangementType =
  | 'editorial'
  | 'predefined'
  | 'userdefined'
  | 'empty';

export interface Arrangement {
  uuid: string;
  streamCode: string;
  code: string;
  language: string;
  arrangementCode?: string;
  type: ArrangementType;
  title: string;
  fullTitle: string;
  description: string;
  contentIdentifier: string;
  method: string;
  methodObject: Method;
  createdAt: string;
  creator?: string;
  createdByUser?: string;
  updatedAt: string;
  contentLicense?: ContentLicense;
  revision: number;
  audienceGroups: Array<Group['id']>;
  isReferenceMaterial: boolean;
  userDelete?: boolean;
  hasAlcarta?: boolean;
  learningLevels?: Array<string>;
  schoolYears?: Array<string>;
  authors?: Array<string>;
  referenceMaterialObjects?: Array<ReferenceMaterialObject>;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  lastModifiedByUser?: string;
  publicationStatus?: string;
  publicationSource?: 'gcuf' | 'alex' | 'gcuf2' | 'alex_manifest';
  newUntil?: string | null;
  availableUntil?: string | null;
  backgroundImage?: string | null;
  backgroundImageCopyright?: string | null;
  hasInstructorMaterials: boolean;
  hasStudentMaterials: boolean;
  deeplink?: string;
}

export interface PersonalizedArrangement extends Arrangement {
  createdByUser?: string;
  lastModifiedByUser?: string;
}

export interface Cluster {
  arrangementCodes: Array<string>;
  methodSlug: string;
  methodName: string;
  code: string;
  name: string;
}

export interface ClusterWithArrangements extends Cluster {
  arrangements: Array<Arrangement>;
}

export interface MethodArrangement extends Arrangement {
  isFavorite: boolean;
  isRecent: boolean;
  lastVisitDate?: string;
}

export interface MethodClusterWithArrangements extends ClusterWithArrangements {
  arrangements: Array<MethodArrangement>;
}

export interface MethodDetail extends Method {
  clusters: Array<Cluster>;
  eans: Array<string>;
  cpis: Array<string>;
}

export type ResourceType =
  | 'module'
  | 'chapter'
  | 'paragraph'
  | 'subparagraph'
  | 'itemMap'
  | 'selfTest'
  | 'theoryMap'
  | 'arrangement'
  | 'source'
  | 'theory'
  | 'reading'
  | 'book'
  | 'terms'
  | 'reference-book'
  | 'sourceMap'
  | 'question'
  | 'conclusion'
  | 'introduction'
  | 'glossentry'
  // @TODO: remove "PO" resource types
  | 'blok'
  | 'week'
  | 'les'
  | 'exam'
  | 'control-exam';

export type AudienceType = 'instructor';

export const SequenceResourceTypes: Array<ResourceType> = [
  'selfTest',
  'exam',
  'control-exam',
];

export interface SourceResource {
  contentIdentifier: string;
  title: string;
}

export interface DisplayMetadata {
  [key: string]: string;
}

export interface InstructorAsset {
  name: string;
  type: 'download' | 'link';
  category: string;
  url: string;
  extension?: string;
}

export interface NavigationMetadata {
  number?: string;
  scoreType?: ScoreType;
  contentPath: string;
  contentType?: string;
  questionType?: InteractionType;
  hasTheory?: boolean;
  hasSources?: boolean;
  hasTerms?: boolean;
  hasResultables?: boolean;
  sources?: Array<SourceResource>;
  backgroundImage?: string;
  backgroundImageCopyright?: string;
  backgroundThumbnail?: string;
  editionDeeplink?: string;
  position?: number;
  audience?: AudienceType;
  links?: Array<string>;
}

export interface EditionMetadata {
  glueContent?: string;
  glueContentHtml?: string;
  assessment?: 'intro' | 'feedback';
}

export interface StreamMetadata {
  code: string;
  name: string;
  methodObject?: Method;
  hasAutomaticStructureNumbering?: boolean;
  hasAutomaticQuestionNumbering?: boolean;
  isModuleStream: boolean;
}

export interface LearningMetadata {
  language?: string;
  learningLevels?: Array<string>;
  subjects?: Array<string>;
  cefrQualifications?: Array<string>;
  referenceLevel?: string;
  intendedEndUserRole?: string;
  typicalLearningTime?: string;
  studentLearningGoals?: Array<string>;
  workingMethod?: Array<string>;
  isAdaptive?: boolean;
  links?: Array<string>;
}

export interface LearningGoal {
  identifier: string;
  path?: string;
  title: string;
}

export interface SmartMetadata {
  keywords?: Array<string>;
  learningGoals?: Array<LearningGoal>;
  repetitionLearningGoals?: Array<string>;
  priorKnowledgeLearningGoals?: Array<string>;
  latestLearningGoalElementRevision?: number;
  assignmentElementLearningGoals?: Array<string>;
  learningGoalElements?: Array<string>;
  mboQualifications?: Array<string>;
  mboCourseStructure?: Array<string>;
  mboTechnical?: Array<string>;
  mboTouchTech?: Array<string>;
  mboDoorEngels?: Array<string>;
  mboDoorNederlands?: Array<string>;
  mboWorkProcesses?: Array<string>;
  mboSubjectArea?: Array<string>;
  lifeSkills?: Array<string>;
  learningStrategies?: Array<string>;
  mboProfile?: Array<string>;
  musicDifficulty?: Array<string>;
  musicStyles?: Array<string>;
  isGuidedPractice?: boolean;
  isOpenQuestionAssignment?: boolean;
  themes?: Array<string>;
  rhvClassifications?: Array<string>;
  allLearningGoals?: Array<LearningGoal>;
}

export interface UserMetadata {
  adaptivity?: string;
  reviewDisabled?: boolean;
  disabled?: boolean;
}

export interface MergedMetadata {
  learningMetadata?: LearningMetadata;
  smartMetadata?: SmartMetadata;
  streamMetadata?: StreamMetadata;
}

export interface Term {
  id: string;
  definition?: string;
  term?: string;
  language?: null;
}

export interface ResourceIdentifierWithType {
  contentIdentifier: string;
  digitalResourceType: ResourceType;
}

export interface ArrangementResource {
  code: string;
  title: string;
  revision: number;
  contentIdentifier: string;
  alexContentIdentifier?: string;
  thedContentIdentifier?: string;
  digitalResourceType: ResourceType;
  navigationMetadata: Partial<
    Pick<
      NavigationMetadata,
      | 'number'
      | 'contentPath'
      | 'contentType'
      | 'audience'
      | 'backgroundImage'
      | 'backgroundImageCopyright'
      | 'hasSources'
      | 'hasTerms'
      | 'hasResultables'
      | 'links'
    >
  >;
  learningMetadata?: Pick<LearningMetadata, 'language' | 'typicalLearningTime'>;
  streamMetadata?: StreamMetadata;
  children?: Array<ArrangementResource>;
  parent?: ArrangementResource;
  parentIdentifier?: string; // Custom addition to allow parent lookup
  instructorAssets?: Array<InstructorAsset>;
}

export interface Resource extends ArrangementResource {
  editionMetadata?: EditionMetadata;
  displayMetadata?: DisplayMetadata;
  learningMetadata?: LearningMetadata;
  mergedMetadata?: MergedMetadata;
  smartMetadata?: SmartMetadata;
  userMetadata?: UserMetadata;
  description?: string;
  fullTitle: string;
  children?: Array<Resource>;
  navigationMetadata: NavigationMetadata;
  originalChildren?: Array<ResourceIdentifierWithType>;
  parent?: Resource;
  parents?: Array<ResourceIdentifierWithType>;
  terms?: Array<Term>;
}

export type ResourceHierarchical = Resource &
  Required<Pick<Resource, 'parent' | 'children'>>;

export interface ResourceInput {
  contentIdentifier: string;
  children?: Array<ResourceInput>;
}

export interface ResourceWithMethodSlug extends Resource {
  methodSlug: Method['slug'];
}

export interface ResourceWithAdaptiveBlocks extends Resource {
  adaptiveBlocks?: Array<Resource>;
}

export interface ResourceWithProgressItem extends Resource {
  progress?: ProgressItem;
}

export interface SearchResultItemParent {
  contentIdentifier: string;
  title: string;
  editionDeeplink?: string;
  digitalResourceType: ResourceType;
}

export interface SearchResultItem {
  arrangementCode: string;
  contentIdentifier: string;
  digitalResourceType: ResourceType;
  title: string;
  snippet: string;
  editionDeeplink?: string;
  parents: Array<SearchResultItemParent>;
}

export interface SearchResultResults {
  count: number;
  items: Array<SearchResultItem>;
  scrollId?: string | null;
}

export interface SearchResultFacetCount {
  count: number;
  value: string;
}

export interface SearchResultFacet {
  facet: Array<SearchResultFacetCount>;
  field: string;
}

export interface SearchResult {
  results: SearchResultResults;
  facets: Array<SearchResultFacet>;
}

export interface VdexNode {
  identifier: string;
  category?: string;
  title: string;
  vdexListIdentifier: string;
  children: Array<VdexNode>;
}

export interface ContentItem {
  identifier: string;
  revision: number;
  xmlSource: string;
  content: string;
}

export * from './createArrangementInterface';
export * from './createArrangementResponseInterface';
export * from './updateArrangementInterface';
